import React, { Component } from 'react';
import './LiveUrlTrackingErrorBoundary.css';

class LiveUrlTrackingErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      resetKey: props.resetKey || 0
    };
    this.retryButtonRef = React.createRef();
  }

  static getDerivedStateFromError(error) {
    // Update state to render error UI
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    console.error('LiveUrlTracking Error Boundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    });

    // Report error to tracking service if available
    if (global.errorTracking && typeof global.errorTracking.captureException === 'function') {
      global.errorTracking.captureException(error, {
        tags: {
          component: 'LiveUrlTracking'
        },
        contexts: {
          react: {
            componentStack: errorInfo.componentStack
          }
        }
      });
    }

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // Focus on retry button when error occurs for accessibility
    if (!prevState.hasError && this.state.hasError && this.retryButtonRef.current) {
      setTimeout(() => {
        this.retryButtonRef.current?.focus();
      }, 100);
    }
  }

  // Reset error boundary when key prop changes
  static getDerivedStateFromProps(nextProps, prevState) {
    // Reset error state if key prop changes (for manual resets)
    if (nextProps.resetKey !== prevState.resetKey) {
      return {
        hasError: false,
        error: null,
        errorInfo: null,
        resetKey: nextProps.resetKey
      };
    }
    return null;
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  getErrorMessage(error) {
    if (!error) return 'An unexpected error occurred';

    // Provide user-friendly messages based on error type
    if (error.name === 'NetworkError' || error.message?.includes('network')) {
      return 'Network connection failed. Please check your internet connection and try again.';
    }

    if (error.message?.includes('timeout')) {
      return 'The request took too long. Please try again.';
    }

    if (error.message?.includes('Cannot read property') || error.message?.includes('Cannot read properties')) {
      return 'There was an issue loading the data. Please try refreshing the page.';
    }

    return error.message || 'An unexpected error occurred';
  }

  render() {
    if (this.state.hasError) {
      const isDevelopment = process.env.NODE_ENV === 'development';
      const errorMessage = this.getErrorMessage(this.state.error);

      return (
        <div className="live-url-error-boundary" data-testid="error-boundary-fallback">
          <div className="error-boundary-container">
            <div className="error-boundary-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
            </div>
            
            <h3 className="error-boundary-title">
              Something went wrong with Live URL Tracking
            </h3>
            
            <div 
              className="error-boundary-message" 
              data-testid="error-boundary-message"
              role="alert"
              aria-live="assertive"
            >
              {errorMessage}
            </div>

            <button
              ref={this.retryButtonRef}
              className="error-boundary-retry-button"
              data-testid="error-boundary-retry"
              onClick={this.handleRetry}
              aria-label="Retry loading Live URL Tracking"
            >
              Try Again
            </button>

            {isDevelopment && this.state.error && (
              <details className="error-boundary-details" data-testid="error-boundary-details">
                <summary>Error Details (Development Mode)</summary>
                <div className="error-details-content">
                  <strong>Error:</strong> {this.state.error.toString()}
                  
                  {this.state.error.stack && (
                    <>
                      <br />
                      <strong>Stack Trace:</strong>
                      <pre className="error-stack">{this.state.error.stack}</pre>
                    </>
                  )}
                  
                  {this.state.errorInfo?.componentStack && (
                    <>
                      <br />
                      <strong>Component Stack:</strong>
                      <pre className="error-component-stack">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default LiveUrlTrackingErrorBoundary;